
import { supabase } from "@/integrations/supabase/client";

interface LocationData {
  latitude: number;
  longitude: number;
  address?: string;
  city?: string;
  country?: string;
}

interface UserLocation {
  id: string;
  user_id: string;
  latitude: number;
  longitude: number;
  address?: string;
  city?: string;
  country?: string;
  is_shared: boolean;
  shared_until?: string;
  created_at: string;
  updated_at: string;
}

// Haversine formula to calculate distance between two points
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Add location precision for privacy
function addLocationPrecision(lat: number, lng: number, precisionMeters: number): { lat: number; lng: number } {
  const latOffset = (Math.random() - 0.5) * 2 * (precisionMeters / 111000); // roughly 111km per degree
  const lngOffset = (Math.random() - 0.5) * 2 * (precisionMeters / (111000 * Math.cos(lat * Math.PI / 180)));
  
  return {
    lat: lat + latOffset,
    lng: lng + lngOffset
  };
}

export const locationService = {
  // Get current user's location using browser API
  async getCurrentLocation(): Promise<LocationData> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          try {
            // Try to get address from coordinates using reverse geocoding
            const address = await this.reverseGeocode(latitude, longitude);
            resolve({
              latitude,
              longitude,
              ...address
            });
          } catch (error) {
            // If reverse geocoding fails, still return coordinates
            resolve({ latitude, longitude });
          }
        },
        (error) => {
          reject(new Error(`Geolocation error: ${error.message}`));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  },

  // Reverse geocoding to get address from coordinates
  async reverseGeocode(lat: number, lng: number): Promise<{ address?: string; city?: string; country?: string }> {
    try {
      // Using OpenStreetMap Nominatim API (free)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );
      
      if (!response.ok) {
        throw new Error('Geocoding failed');
      }
      
      const data = await response.json();
      return {
        address: data.display_name,
        city: data.address?.city || data.address?.town || data.address?.village,
        country: data.address?.country
      };
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
      return {};
    }
  },

  // Save user location to database
  async saveUserLocation(userId: string, locationData: LocationData, isShared: boolean = false, sharedUntil?: Date): Promise<UserLocation> {
    const { data, error } = await supabase
      .from('user_locations')
      .upsert({
        user_id: userId,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        address: locationData.address,
        city: locationData.city,
        country: locationData.country,
        is_shared: isShared,
        shared_until: sharedUntil?.toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get user's saved location
  async getUserLocation(userId: string): Promise<UserLocation | null> {
    const { data, error } = await supabase
      .from('user_locations')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  // Calculate distance between users with privacy precision
  async calculateUserDistance(userId1: string, userId2: string): Promise<number | null> {
    try {
      const [location1, location2, profile1, profile2] = await Promise.all([
        this.getUserLocation(userId1),
        this.getUserLocation(userId2),
        supabase.from('user_profiles').select('location_precision_meters').eq('user_id', userId1).single(),
        supabase.from('user_profiles').select('location_precision_meters').eq('user_id', userId2).single()
      ]);

      if (!location1 || !location2) return null;

      // Apply privacy precision
      const precision1 = profile1.data?.location_precision_meters || 300;
      const precision2 = profile2.data?.location_precision_meters || 300;

      const fuzzyLocation1 = addLocationPrecision(location1.latitude, location1.longitude, precision1);
      const fuzzyLocation2 = addLocationPrecision(location2.latitude, location2.longitude, precision2);

      return calculateDistance(
        fuzzyLocation1.lat,
        fuzzyLocation1.lng,
        fuzzyLocation2.lat,
        fuzzyLocation2.lng
      );
    } catch (error) {
      console.error('Error calculating user distance:', error);
      return null;
    }
  },

  // Share location temporarily (for messaging)
  async shareLocationTemporarily(userId: string, duration: number = 3600): Promise<UserLocation> {
    const location = await this.getCurrentLocation();
    const sharedUntil = new Date(Date.now() + duration * 1000);
    
    return this.saveUserLocation(userId, location, true, sharedUntil);
  },

  // Check if user has location permission
  async checkLocationPermission(): Promise<PermissionState> {
    if (!navigator.permissions) {
      return 'prompt';
    }
    
    const permission = await navigator.permissions.query({ name: 'geolocation' });
    return permission.state;
  },

  // Request location permission
  async requestLocationPermission(): Promise<boolean> {
    try {
      await this.getCurrentLocation();
      return true;
    } catch (error) {
      console.error('Location permission denied:', error);
      return false;
    }
  }
};
